import { useState, useCallback, useEffect, useRef } from "react";
import { getStaticTranslationFallback } from "../constants/static-translations";
import { apiService } from "../services/api.service";
import type { StaticTextTranslator } from "../utils";

interface UseTranslationReturn {
  /** Translate an English string. Returns cached translation or falls back to English. */
  t: StaticTextTranslator;
  /** Pre-load translations for a batch of strings. */
  preloadTranslations: (texts: string[]) => Promise<void>;
  /** Whether the current language is English (no translation needed). */
  isEnglish: boolean;
}

export function useTranslation(
  languageCode: string | null,
): UseTranslationReturn {
  const [, setTranslations] = useState<Record<string, string>>({});
  const translationsRef = useRef<Record<string, string>>({});
  const languageRef = useRef<string | null>(languageCode);

  const isEnglish = !languageCode || languageCode.startsWith("en");

  useEffect(() => {
    languageRef.current = languageCode;
    translationsRef.current = {};
    setTranslations({});
  }, [languageCode]);

  const preloadTranslations = useCallback(
    async (texts: string[]) => {
      if (isEnglish || !languageCode) return;

      const untranslated = [...new Set(texts)].filter(
        (text) =>
          !translationsRef.current[text] &&
          !getStaticTranslationFallback(languageCode, text),
      );
      if (untranslated.length === 0) return;

      try {
        const results = await apiService.translateTexts(
          untranslated,
          languageCode,
        );
        if (languageRef.current !== languageCode) {
          return;
        }
        const newTranslations: Record<string, string> = {};
        untranslated.forEach((text, i) => {
          newTranslations[text] = results[i] ?? text;
        });

        translationsRef.current = {
          ...translationsRef.current,
          ...newTranslations,
        };
        setTranslations((prev) => ({ ...prev, ...newTranslations }));
      } catch {
        // Fallback to English on error — no-op
      }
    },
    [languageCode, isEnglish],
  );

  const t = useCallback(
    (englishText: string): string => {
      if (isEnglish) return englishText;
      return (
        translationsRef.current[englishText] ??
        getStaticTranslationFallback(languageCode, englishText) ??
        englishText
      );
    },
    [languageCode, isEnglish],
  );

  return { t, preloadTranslations, isEnglish };
}
